import { guards } from '@shared/helpers/typeguards.helpers';
import { MapStoreyProperty } from '../models/map-feature.models';

export type BuildingFillHit = {
  buildingId: string;
  /** Roof elevation of the hit storey, metres */
  topHeight: number;
};

export type SelectedBuildingVolumeClickAction =
  | { kind: 'select-building'; buildingId: string }
  | { kind: 'select-storey'; storeyId: string };

/** Select an unselected building first; subsequent clicks choose the highest hit floor. */
export const resolveSelectedBuildingVolumeClickAction = (input: {
  clickedStoreyId: string | undefined;
  currentBuildingId: string | undefined;
  selectedStoreyId: string | undefined;
  isBuildingOutlineSelected: boolean;
}): SelectedBuildingVolumeClickAction | undefined => {
  if (!input.clickedStoreyId) return undefined;
  if (
    !input.selectedStoreyId &&
    !input.isBuildingOutlineSelected &&
    input.currentBuildingId
  ) {
    return {
      kind: 'select-building',
      buildingId: input.currentBuildingId,
    };
  }
  if (
    input.isBuildingOutlineSelected ||
    input.clickedStoreyId !== input.selectedStoreyId
  ) {
    return { kind: 'select-storey', storeyId: input.clickedStoreyId };
  }
  return undefined;
};

type FeatureLike = {
  properties?: { [name: string]: unknown } | null;
};

/**
 * Read building id + roof height from a queried fill feature.
 * {@link fallbackBuildingId} is used for the selected building layer, which
 * has no `buildingId` property.
 */
export const toBuildingFillHit = (
  feature: FeatureLike,
  fallbackBuildingId?: string,
): BuildingFillHit | undefined => {
  const properties = feature.properties;
  const rawId = properties?.buildingId ?? fallbackBuildingId;
  if (!guards.is.string(rawId) || rawId === '') return undefined;
  const topHeight = Number(properties?.[MapStoreyProperty.TopHeight]);
  return {
    buildingId: rawId,
    topHeight: Number.isFinite(topHeight) ? topHeight : 0,
  };
};

/**
 * Map queried fill features to {@link BuildingFillHit} rows.
 */
export const toBuildingFillHits = (
  features: readonly FeatureLike[],
  fallbackBuildingId?: string,
): BuildingFillHit[] =>
  features.flatMap((feature) => {
    const hit = toBuildingFillHit(feature, fallbackBuildingId);
    return hit ? [hit] : [];
  });

/**
 * Building whose hit storey sits highest. Later ids in {@link buildingOrder}
 * win when roof heights are equal (later list entries are drawn on top).
 * Returns a {@link BuildingFillHit}, or `undefined` when {@link hits} is empty.
 */
export const pickTopmostBuildingHit = (
  hits: readonly BuildingFillHit[],
  buildingOrder: readonly string[] = [],
): BuildingFillHit | undefined => {
  if (!hits.length) return undefined;

  const topById = new Map<string, number>();
  for (const hit of hits) {
    const current = topById.get(hit.buildingId);
    if (current === undefined || hit.topHeight > current) {
      topById.set(hit.buildingId, hit.topHeight);
    }
  }

  let best: BuildingFillHit | undefined;
  let bestOrder = Number.NEGATIVE_INFINITY;
  for (const [buildingId, topHeight] of topById) {
    const order = buildingOrder.indexOf(buildingId);
    if (
      !best ||
      topHeight > best.topHeight ||
      (topHeight === best.topHeight && order >= bestOrder)
    ) {
      best = { buildingId, topHeight };
      bestOrder = order;
    }
  }
  return best;
};
